/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Dropdown,
    Field,
    Input,
    makeStyles,
    Option,
    Spinner,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    tokens
} from '@fluentui/react-components';
import { Add24Regular, CheckmarkCircle24Regular, Dismiss24Filled, Dismiss24Regular, Edit24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ExcelExportButton from '../../components/ExcelExportButton';
import type { ChangeStatusForschungsschwerpunktRequest } from '../../models/ChangeStatusForschungsschwerpunktRequest';
import type { ProjektForschungsschwerpunktItem } from '../../models/ProjektForschungsschwerpunktItem';
import { DataService } from '../../services/dataService';
import type { ExcelExportColumn } from '../../utils/excelExport';

const EXCEL_COLUMNS: ExcelExportColumn<ProjektForschungsschwerpunktItem>[] = [
    { header: 'Code', value: (item) => item.id, width: 10 },
    { header: 'Name', value: (item) => item.bezeichnung, width: 50 },
    { header: 'Department', value: (item) => (item.departmentMetaId == null ? '' : item.departmentBezeichnung), width: 40 },
    { header: 'Status', value: (item) => (item.istAktiv ? 'Active' : 'Inactive'), width: 12 },
];

type SortDirection = 'asc' | 'desc' | null;
type SortColumn = keyof ProjektForschungsschwerpunktItem | null;

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: tokens.spacingHorizontalL,
        gap: tokens.spacingVerticalL,
    },
    headerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: tokens.spacingHorizontalL,
    },
    filterContainer: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        alignItems: 'center',
        flex: 1,
    },
    searchInput: {
        flex: 1,
        maxWidth: '400px',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        minHeight: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        flex: 1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
    },
    tableRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    actionButtons: {
        display: 'flex',
        gap: tokens.spacingHorizontalXS,
    },
    colActions: {
        width: '100px',
        minWidth: '100px',
    },
    colCode: {
        width: '120px',
        minWidth: '100px',
    },
    colName: {
        width: '35%',
        minWidth: '200px',
    },
    colDepartment: {
        width: '35%',
        minWidth: '200px',
    },
    colStatus: {
        width: '15%',
        minWidth: '100px',
    },
    // Drawer styles
    section: {
        marginBottom: tokens.spacingVerticalL,
    },
    sectionTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        marginBottom: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground1,
    },
    formGroup: {
        display: 'grid',
        gap: tokens.spacingVerticalM,
        marginBottom: tokens.spacingVerticalL,
    },
    fieldRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingHorizontalM,
    },
    fullWidth: {
        gridColumn: '1 / -1',
    },
    switchField: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
    },
    footerActions: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        justifyContent: 'flex-end',
        paddingTop: tokens.spacingVerticalL,
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    },
});

const DEPARTMENT_OPTIONS = [
    { value: 0, label: 'Not Assigned' },
    { value: 1, label: 'IMC Business School' },
    { value: 2, label: 'Department of Health Sciences' },
    { value: 3, label: 'Department of Science & Technology' },
    { value: 4, label: 'International Relations' },
    { value: 5, label: 'Hochschulservices' },
];

interface DrawerState {
    isOpen: boolean;
    item: ProjektForschungsschwerpunktItem | null;
    isEditMode: boolean;
}

const KeyResearchAreaTab: React.FC = () => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();

    const [items, setItems] = useState<ProjektForschungsschwerpunktItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [sortColumn, setSortColumn] = useState<SortColumn>('bezeichnung');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [drawerState, setDrawerState] = useState<DrawerState>({
        isOpen: false,
        item: null,
        isEditMode: false,
    });
    const [formData, setFormData] = useState<ProjektForschungsschwerpunktItem>({
        id: 0,
        bezeichnung: '',
        istAktiv: true,
        departmentMetaId: undefined,
        departmentBezeichnung: '',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<ProjektForschungsschwerpunktItem | null>(null);

    const dataService = React.useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch all items
    useEffect(() => {
        const fetchItems = async () => {
            if (!dataService) {
                return;
            }

            try {
                setIsLoading(true);
                const data = await dataService.getForschungsschwerpunkteList();
                console.log('Fetched research areas:', data);
                setItems(data || []);
            } catch (err) {
                console.error('Error loading research areas:', err);
                setItems([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchItems();
    }, [dataService]);

    // Filter items based on search text
    const filteredItems = useMemo(() => {
        let results = items;

        // Apply search filter
        if (filterText.trim()) {
            const searchLower = filterText.toLowerCase();
            results = results.filter((item) => {
                return (
                    item.bezeichnung?.toLowerCase().includes(searchLower) ||
                    item.departmentBezeichnung?.toLowerCase().includes(searchLower)
                );
            });
        }

        // Apply sorting
        if (sortColumn) {
            results = [...results].sort((a, b) => {
                let aValue: any = a[sortColumn];
                let bValue: any = b[sortColumn];

                // Handle null/undefined
                if (aValue == null) aValue = '';
                if (bValue == null) bValue = '';

                // Convert to lowercase for string comparison
                if (typeof aValue === 'string') aValue = aValue.toLowerCase();
                if (typeof bValue === 'string') bValue = bValue.toLowerCase();

                // Comparison
                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return results;
    }, [items, filterText, sortColumn, sortDirection]);

    const handleSort = useCallback((column: SortColumn) => {
        if (sortColumn === column) {
            // Toggle direction or clear sort
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortColumn('bezeichnung');
                setSortDirection('asc');
            }
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    }, [sortColumn, sortDirection]);

    const handleAddNew = () => {
        setFormData({
            id: 0,
            bezeichnung: '',
            istAktiv: true,
            departmentMetaId: undefined,
            departmentBezeichnung: '',
        });
        setDrawerState({
            isOpen: true,
            item: null,
            isEditMode: false,
        });
    };

    const handleView = (item: ProjektForschungsschwerpunktItem) => {
        if (item.departmentMetaId === null || item.departmentMetaId === undefined) {
            item.departmentMetaId = 0;
        }
        setFormData(item);
        setDrawerState({
            isOpen: true,
            item: item,
            isEditMode: false,
        });
    };

    const handleEdit = (item: ProjektForschungsschwerpunktItem) => {
        setFormData(item);
        setDrawerState({
            isOpen: true,
            item: item,
            isEditMode: true,
        });
    };

    const handleRowDoubleClick = (item: ProjektForschungsschwerpunktItem) => {
        handleView(item);
    };

    const handleDrawerClose = () => {
        setDrawerState({
            isOpen: false,
            item: null,
            isEditMode: false,
        });
        setFormData({
            id: 0,
            bezeichnung: '',
            istAktiv: true,
            departmentMetaId: undefined,
            departmentBezeichnung: '',
        });
    };

    const handleDrawerSave = async () => {
        if (!dataService) return;

        if (!formData.bezeichnung?.trim()) {
            alert('Please enter a name for the research area');
            return;
        }

        try {
            setIsSaving(true);

            if (drawerState.isEditMode && drawerState.item) {
                // Update existing
                formData.departmentMetaId = formData.departmentMetaId === 0 ? undefined : formData.departmentMetaId;
                await dataService.updateForschungsschwerpunkt(formData);
                setItems((prevItems) =>
                    prevItems.map((p) => (p.id === formData.id ? formData : p))
                );
            } else {
                // Add new
                await dataService.addForschungsschwerpunkt(formData);
                // Reload the list
                const data = await dataService.getForschungsschwerpunkteList();
                setItems(data || []);
            }

            handleDrawerClose();
        } catch (err) {
            console.error('Error saving research area:', err);
            alert('Error saving research area: ' + (err instanceof Error ? err.message : 'Unknown error'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteClick = (item: ProjektForschungsschwerpunktItem) => {
        setItemToDelete(item);
        setShowDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (!dataService || !itemToDelete) return;

        try {
            setIsSaving(true);
            const request: ChangeStatusForschungsschwerpunktRequest = {
                forschungsschwerpunktCode: itemToDelete.id,
                isLocked: !itemToDelete.istAktiv,
            };
            await dataService.changeStatusForschungsschwerpunkt(request);

            // Update the item in the list
            setItems((prevItems) =>
                prevItems.map((p) =>
                    p.id === itemToDelete.id ? { ...p, istAktiv: !p.istAktiv } : p
                )
            );

            setShowDeleteDialog(false);
            setItemToDelete(null);
        } catch (err) {
            console.error('Error changing research area status:', err);
            alert('Error changing status: ' + (err instanceof Error ? err.message : 'Unknown error'));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={styles.container}>
            {/* Header with Filter and Add Button */}
            <div className={styles.headerContainer}>
                <div className={styles.filterContainer}>
                    <Input
                        placeholder="Search by name or department..."
                        value={filterText}
                        onChange={(_, data) => setFilterText(data.value)}
                        className={styles.searchInput}
                    />
                    <ExcelExportButton
                        items={filteredItems}
                        columns={EXCEL_COLUMNS}
                        fileName={`KeyResearchAreas_${new Date().toISOString().split('T')[0]}`}
                        sheetName="Key Research Areas"
                    />
                </div>
                <Button
                    icon={<Add24Regular />}
                    appearance="primary"
                    onClick={handleAddNew}
                >
                    Add Research Area
                </Button>
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading research areas..." />
                </div>
            )}

            {/* Empty State */}
            {!isLoading && items.length === 0 && (
                <div className={styles.emptyState}>
                    No research areas found. Click "Add Research Area" to create one.
                </div>
            )}

            {/* Items Table */}
            {!isLoading && filteredItems.length > 0 && (
                <div className={styles.tableContainer}>
                    <Table size="small">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell className={styles.colActions}>
                                    Actions
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className={styles.colCode}
                                    onClick={() => handleSort('id')}
                                >
                                    Code
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className={styles.colName}
                                    onClick={() => handleSort('bezeichnung')}
                                >
                                    Name
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className={styles.colDepartment}
                                    onClick={() => handleSort('departmentBezeichnung')}
                                >
                                    Department
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className={styles.colStatus}
                                    onClick={() => handleSort('istAktiv')}
                                >
                                    Status
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredItems.map((item) => (
                                <TableRow
                                    key={item.id}
                                    className={styles.tableRow}
                                    onDoubleClick={() => handleRowDoubleClick(item)}
                                >
                                    <TableCell>
                                        <TableCellLayout>
                                            <div className={styles.actionButtons}>
                                                <Button
                                                    icon={<Edit24Regular />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEdit(item);
                                                    }}
                                                    appearance="subtle"
                                                    size="small"
                                                    title="Edit"
                                                />
                                                <Button
                                                    icon={item.istAktiv ? <Dismiss24Filled /> : <CheckmarkCircle24Regular />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteClick(item);
                                                    }}
                                                    appearance="subtle"
                                                    size="small"
                                                    title={item.istAktiv ? 'Deactivate' : 'Activate'}
                                                />
                                            </div>
                                        </TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.id || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.bezeichnung || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.departmentMetaId == null ? '-' : item.departmentBezeichnung || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>
                                            {item.istAktiv ? (
                                                <span style={{ color: tokens.colorPaletteGreenForeground1 }}>
                                                    Active
                                                </span>
                                            ) : (
                                                <span style={{ color: tokens.colorPaletteRedForeground1 }}>
                                                    Inactive
                                                </span>
                                            )}
                                        </TableCellLayout>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* No results after filtering */}
            {!isLoading && items.length > 0 && filteredItems.length === 0 && (
                <div className={styles.emptyState}>
                    No research areas match your search
                </div>
            )}

            {/* Drawer for Add/Edit */}
            <Drawer
                open={drawerState.isOpen}
                onOpenChange={(_, { open }) => {
                    if (!open) handleDrawerClose();
                }}
                size="medium"
                type="overlay"
                position="end"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="Close"
                                icon={<Dismiss24Regular />}
                                onClick={handleDrawerClose}
                            />
                        }
                    >
                        {drawerState.isEditMode ? 'Edit Research Area' : drawerState.item ? 'View Research Area' : 'Add Research Area'}
                    </DrawerHeaderTitle>
                </DrawerHeader>

                <DrawerBody>
                    <div className={styles.section}>
                        <div className={styles.formGroup}>
                            <Field label="Code">
                                <Input
                                    value={formData.id.toString() || ''}
                                    onChange={(_, data) =>
                                        setFormData({ ...formData, id: parseInt(data.value, 10) })
                                    }
                                    disabled={drawerState.isEditMode}
                                    placeholder=""
                                />
                            </Field>
                            <Field label="Name" required>
                                <Input
                                    value={formData.bezeichnung || ''}
                                    onChange={(_, data) =>
                                        setFormData({ ...formData, bezeichnung: data.value })
                                    }
                                    disabled={!drawerState.isEditMode && drawerState.item !== null}
                                    placeholder="Enter research area name"
                                />
                            </Field>

                            <Field label="Department">
                                <Dropdown
                                    value={String(formData.departmentMetaId ? formData.departmentBezeichnung : 'Not Assigned')}
                                    onOptionSelect={(_, data) => {
                                        const selectedId = Number(data.optionValue);
                                        const selectedDept = DEPARTMENT_OPTIONS.find(d => d.value === selectedId);

                                        setFormData({
                                            ...formData,
                                            departmentMetaId: selectedId === 0 ? undefined : selectedId,
                                            departmentBezeichnung: selectedDept?.label || ""
                                        });
                                    }}
                                    disabled={!drawerState.isEditMode && drawerState.item !== null}
                                >
                                    {DEPARTMENT_OPTIONS.map(dept => (
                                        <Option key={dept.value} value={String(dept.value)}>
                                            {dept.label}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </Field>

                            {drawerState.isEditMode && (
                                <div className={styles.switchField}>
                                    <Switch
                                        checked={formData.istAktiv}
                                        onChange={(_, data) =>
                                            setFormData({ ...formData, istAktiv: data.checked })
                                        }
                                        label={formData.istAktiv ? 'Active' : 'Inactive'}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Footer Actions */}
                        <div className={styles.footerActions}>
                            <Button
                                appearance="secondary"
                                onClick={handleDrawerClose}
                                disabled={isSaving}
                            >
                                {drawerState.isEditMode || !drawerState.item ? 'Cancel' : 'Close'}
                            </Button>
                            {(drawerState.isEditMode || !drawerState.item) && (
                                <Button
                                    appearance="primary"
                                    onClick={handleDrawerSave}
                                    disabled={isSaving}
                                >
                                    {isSaving ? <Spinner size="tiny" /> : 'Save'}
                                </Button>
                            )}
                        </div>
                    </div>
                </DrawerBody>
            </Drawer>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={(_, data) => setShowDeleteDialog(data.open)}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>
                            {itemToDelete?.istAktiv ? 'Deactivate' : 'Activate'} Research Area
                        </DialogTitle>
                        <DialogContent>
                            Are you sure you want to{' '}
                            {itemToDelete?.istAktiv ? 'deactivate' : 'activate'}{' '}
                            <strong>{itemToDelete?.bezeichnung}</strong>?
                        </DialogContent>
                        <div
                            style={{
                                display: 'flex',
                                gap: tokens.spacingHorizontalM,
                                justifyContent: 'flex-end',
                                paddingTop: tokens.spacingVerticalL,
                            }}
                        >
                            <Button
                                appearance="secondary"
                                onClick={() => setShowDeleteDialog(false)}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                appearance="primary"
                                onClick={handleConfirmDelete}
                                disabled={isSaving}
                            >
                                {isSaving ? <Spinner size="tiny" /> : itemToDelete?.istAktiv ? 'Deactivate' : 'Activate'}
                            </Button>
                        </div>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default KeyResearchAreaTab;
